import { AddToBasket, ErrorAddToBasket, ProductNotAddedToBasket } from 'autogen/translation-keys/trans-website-basket';
import { BuyNow } from 'autogen/translation-keys/trans-website-shared-buttons';
import { ButtonWithSpinner } from 'components/Button';
import ButtonWithAnimatedStatuses from 'components/Button/ButtonWithAnimatedStatuses';
import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'components/Toast';
import usePowerstep from 'components/powerstep/shared/hooks/usePowerstep';
import {
    SelectedFrequencyContext,
    SubscriptionContext
} from 'components/pages/PDP/components/MidSection/hooks/context';
import type { ProductActionProps } from '../../shared/types';
import { useAddProduct } from './action';
import useFeatureToggles from 'hooks/user/useFeatureToggles';
import { useFindInStoreQuickView } from 'components/storeList/shared/hooks';
import queryClient from 'setup/react-query/queryClient';
import { ProductDeliveryInfoViewModelQueryKey } from 'autogen/swagger/all/hooks';

export function AddProductButton({ params, trackingInfo, children, ...restProps }: ProductActionProps['BuyNow']) {
    const { t } = useTranslation();
    const [openPowerstep] = usePowerstep();
    const { selectedFrequency } = useContext(SelectedFrequencyContext);
    const { subscriptionActive } = useContext(SubscriptionContext);
    const AddProduct = useAddProduct();
    const isSubscriptionSelected = subscriptionActive && selectedFrequency;
    const { powerstepEnabled } = useFeatureToggles();
    const [, toggle] = useFindInStoreQuickView();

    async function buttonClick() {
        try {
            let addProductParams = params;

            if (isSubscriptionSelected) {
                addProductParams = {
                    ...params,
                    subscriptionFrequencyId: selectedFrequency?.id
                };
            } else if (params.storeId) {
                addProductParams = { ...params, action: 'ClickAndCollect' };
            }

            const { quantityModified } = await AddProduct({ params: addProductParams, trackingInfo });

            if (quantityModified === 0) {
                toast.error<string>(t(ProductNotAddedToBasket), {
                    position: 'top-right'
                });
                return;
            }

            queryClient.invalidateQueries({ queryKey: [ProductDeliveryInfoViewModelQueryKey] });

            if (params.showPowerstepOnAddToBasket && powerstepEnabled) {
                if (params.storeId) {
                    toggle({ isOpen: false });
                }

                openPowerstep([params.productId], params.storeId);
            } else {
                toast.success<string>(t(AddToBasket), {
                    position: 'top-right'
                });
            }
        } catch (e) {
            toast.error<string>(t(ErrorAddToBasket), {
                position: 'top-right'
            });
            throw new Error(e as string);
        }
    }

    return (
        <ButtonWithSpinner onClick={buttonClick} type="button" {...restProps}>
            {children || t(BuyNow)}
        </ButtonWithSpinner>
    );
}

function AddProductButtonCompact({ params, trackingInfo, ...restProps }: ProductActionProps['BuyNow']) {
    const { t } = useTranslation();
    const [openPowerstep] = usePowerstep();
    const AddProduct = useAddProduct();
    const { powerstepEnabled } = useFeatureToggles();

    const AddProductAndSpawnToast = useCallback(async () => {
        try {
            await AddProduct({ params, trackingInfo });

            if (params.showPowerstepOnAddToBasket && powerstepEnabled) {
                openPowerstep([params.productId]);
            } else {
                toast.success<string>(t(AddToBasket), {
                    position: 'top-right'
                });
            }
        } catch (e) {
            toast.error<string>(t(ErrorAddToBasket), {
                position: 'top-right'
            });
            throw new Error(e as string);
        }
    }, [openPowerstep, params, t, trackingInfo, powerstepEnabled]);

    return (
        <ButtonWithAnimatedStatuses
            onClick={AddProductAndSpawnToast}
            type="button"
            aria-label={t(BuyNow)}
            {...restProps}
        />
    );
}
//
export default Object.assign(AddProductButton, {
    Compact: AddProductButtonCompact
});
